// Dependencies
import { FC, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Stack,
  Paper,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';

// Styles
import styles from '../styles';

// APIs
import * as portfolioUploadApi from '../../../apis/portfolio-upload.apis';

// Context
import { useToastContext } from '../../../components/Toast/toastContext';
import { ROUTES } from '../../../contants';
import { PortfolioUploadTabs } from '../components/PortfolioUploadTabs';

// Portfolio Upload Edit Page
export const PortfolioUploadEditPage: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { activeToast } = useToastContext();

  const [loading, setLoading] = useState(true);
  const [uploadedData, setUploadedData] = useState<any>(null);

  // Load existing data
  useEffect(() => {
    const loadData = async () => {
      if (!id) return;

      setLoading(true);
      try {
        const response = await portfolioUploadApi.getPortfolioUpload(id);
        if (response.success && response.data) {
          setUploadedData(response.data);
        }
      } catch (error: any) {
        activeToast({
          type: 'error',
          text: error.msg || 'Failed to load portfolio upload',
        });
        navigate(ROUTES.PORTFOLIO_UPLOAD);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  if (loading) {
    return (
      <Box sx={styles.container}>
        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '400px' }}>
          <CircularProgress />
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Stack spacing={24}>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Typography variant="h4">Edit Portfolio Data from Excel File</Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(ROUTES.PORTFOLIO_UPLOAD)}
          >
            Back
          </Button>
        </Stack>

        {/* Read-only name and ISINs (from file) */}
        {uploadedData && (
          <Paper sx={{ p: 2 }}>
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">Name (from filename)</Typography>
              <Typography variant="body1">{uploadedData.name ?? '-'}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>ISINs (from config sheet)</Typography>
              <Typography variant="body1">
                {Array.isArray(uploadedData.isins) && uploadedData.isins.length > 0
                  ? uploadedData.isins.join(', ')
                  : (uploadedData.isin ?? '-')}
              </Typography>
            </Stack>
          </Paper>
        )}

        {/* Data Display Section */}
        {uploadedData && (
          <Box sx={styles.dataSection}>
            <PortfolioUploadTabs uploadedData={uploadedData} />
          </Box>
        )}

        {/* Action Buttons */}
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(ROUTES.PORTFOLIO_UPLOAD)}
          >
            Back
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};
